import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Tooltip } from "antd";
import { Camera, Send } from "lucide-react";
import { AutocompleteTextarea } from "@components/Common/AutocompleteTextarea";
import ScreengrabCapture from "@components/Screengrab/ScreengrabCapture";
import { useChatContext } from "../context";
import { latestAgentRowById } from "../utils";
import { primeChatImage } from "../hooks/useChatImage";
import { MAX_IMAGES } from "../hooks/useImageAttachments";
import AttachButton from "./AttachButton";
import AttachmentStrip from "./AttachmentStrip";
import type { Attachment, MessageImage, AnswerReply, SendReply, QuestionData } from "../types";

export interface ChatInputHandle {
    answer: (reply: AnswerReply) => void;
}

interface ChatInputProps {
    // React 19 passes ref as a prop. The host owns it: the ref crosses MessageList and ChatInput.
    ref?: React.Ref<ChatInputHandle>;
    onSend: (text: string, images: MessageImage[], reply?: SendReply) => void;
    disabled: boolean;
    conversationEpoch: number;
    attachments: Attachment[];
    setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
    attachmentsRef: React.MutableRefObject<Attachment[]>;
    stageFiles: (files: File[]) => void;
    handleRemove: (id: string) => void;
    // Lets a host (the floating widget) hide itself while the full-page region
    // selection is active. The full /chatbot page doesn't pass it.
    onCaptureOpenChange?: (open: boolean) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
    ref, onSend, disabled, conversationEpoch,
    attachments, setAttachments, attachmentsRef, stageFiles, handleRemove, onCaptureOpenChange,
}) => {
    const [text, setText] = useState("");
    const [capturing, setCapturing] = useState(false);
    const { messages, openQuestionIds } = useChatContext();

    // A typed message answers an open `text` question only when it is the one open question.
    // When a card or another question is also open, the server matches the typed text itself.
    const openTextQuestionId = useMemo(() => {
        if (openQuestionIds.length !== 1) return null;
        const row = latestAgentRowById(messages).get(openQuestionIds[0]);
        const data = row?.type === "question" ? (row.metadata.data as QuestionData) : null;
        return data?.answer_type === "text" ? data.question_id : null;
    }, [messages, openQuestionIds]);

    useEffect(() => {
        onCaptureOpenChange?.(capturing);
    }, [capturing, onCaptureOpenChange]);

    // Attachment staging resets in useImageAttachments (keyed on the same conversationEpoch);
    // text is local to this component and resets here.
    useEffect(() => {
        setText("");
    }, [conversationEpoch]);

    // While a chat is open, a file dropped where nothing takes it must not make the browser
    // open the file and leave the app. Handlers that take a drop (this composer, the message
    // list, a page's own upload zone) call preventDefault first — React dispatches at the
    // root, before window — so the guard only acts on drops nobody handled. dropEffect
    // "none" shows a no-drop cursor; the drop listener is the backstop for a browser that
    // still fires drop. Native file inputs keep their own drop.
    useEffect(() => {
        const guard = (e: DragEvent) => {
            if (!e.dataTransfer?.types.includes("Files") || e.defaultPrevented) return;
            if ((e.target as Element | null)?.closest?.('input[type="file"]')) return;
            e.preventDefault();
            if (e.type === "dragover") e.dataTransfer.dropEffect = "none";
        };
        window.addEventListener("dragover", guard);
        window.addEventListener("drop", guard);
        return () => {
            window.removeEventListener("dragover", guard);
            window.removeEventListener("drop", guard);
        };
    }, []);

    // Capture phase: CodeMirror's own drop handler reads dropped files as text and inserts
    // them, so it must never see a file drop. The contains() check skips events from the
    // ScreengrabCapture overlay — a portal, so in this wrapper's React tree but not its DOM.
    const handleDragOverCapture = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.target as Node) || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
    };

    const handleDropCapture = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.target as Node) || e.dataTransfer.files.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        stageFiles(Array.from(e.dataTransfer.files));
    };

    const handlePaste = useCallback((event: ClipboardEvent) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        const fileItems = items.filter(it => it.kind === "file");
        if (fileItems.length === 0) return false; // no files → let text paste through

        const files = fileItems.map(it => it.getAsFile()).filter((f): f is File => !!f);
        stageFiles(files);
        return true; // suppress default text paste — we handled it (even if nothing was staged)
    }, [stageFiles]);

    const anyUploading = attachments.some(a => a.status === "uploading");
    const canSend = !disabled && !anyUploading && !!text.trim();

    const handleSend = useCallback(() => {
        if (disabled) return;
        const current = attachmentsRef.current;
        if (current.some(a => a.status === "uploading") || !text.trim()) return;
        // Failed uploads were already dropped on error, so everything staged here is "ready".
        const ready = current.filter(a => a.status === "ready" && a.ref);
        ready.forEach(a => primeChatImage(a.ref as string, a.previewUrl)); // ownership → cache; don't revoke
        const trimmed = text.trim();
        const reply = openTextQuestionId && !trimmed.startsWith("/") ? { replyTo: openTextQuestionId } : undefined;
        onSend(trimmed, ready.map(a => ({ ref: a.ref as string, fileName: a.fileName })), reply);
        setText("");
        setAttachments([]);
    }, [disabled, text, onSend, attachmentsRef, setAttachments, openTextQuestionId]);

    // A chip click or a multi-choice Send. The labels always go as the input. The draft and the
    // staged images stay in the composer: the backend reads a draft sent with a click alone.
    const answer = useCallback((reply: AnswerReply) => {
        if (disabled) return;
        const labels = reply.choice ? reply.choice.label : (reply.choices ?? []).map(c => c.label).join(", ");
        onSend(labels, [], {
            replyTo: reply.questionId,
            choice: reply.choice?.id,
            choices: reply.choices?.map(c => c.id),
        });
    }, [disabled, onSend]);

    useImperativeHandle(ref, () => ({ answer }), [answer]);

    return (
        <div className="chat-v2-input-wrapper" onDragOverCapture={handleDragOverCapture} onDropCapture={handleDropCapture}>
            <AttachmentStrip attachments={attachments} onRemove={handleRemove} />
            <div className="chat-v2-input-row">
                <AttachButton
                    disabled={disabled}
                    atMax={attachments.length >= MAX_IMAGES}
                    onFilesSelected={stageFiles}
                />
                <Tooltip
                    title={
                        disabled ? "Wait for the response to finish"
                            : attachments.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images per message`
                                : "Screenshot a region of the page"
                    }
                    overlayClassName="chat-v2-tooltip-sm"
                >
                    {/* Same disabled-button Tooltip workaround as AttachButton. No SCREENGRAB_UI_ATTR
                        here: unlike the float button, this is real page UI and should appear in
                        captures that include the composer. */}
                    <span>
                        <button
                            type="button"
                            className="chat-v2-attach-icon-btn"
                            disabled={disabled || attachments.length >= MAX_IMAGES}
                            aria-label="Screenshot a region"
                            onClick={() => setCapturing(true)}
                        >
                            <Camera size="1em" />
                        </button>
                    </span>
                </Tooltip>
                <AutocompleteTextarea
                    value={text}
                    onChange={setText}
                    onEnter={handleSend}
                    isTyping={disabled}
                    showSubmitButton={false}
                    theme="textarea"
                    rows={2}
                    placeholder="Ask me a question…"
                    ariaLabel="Ask Whistle AI"
                    entityTypes={["players", "metrics", "positions", "playerGroups", "treatments", "treatmentApplicationTime"]}
                    onPaste={handlePaste}
                />
                <Tooltip title={canSend ? "Send message" : undefined} overlayClassName="chat-v2-tooltip-sm" mouseEnterDelay={0.15}>
                    <span>
                        <button
                            type="button"
                            className="chat-v2-input-send-btn"
                            onClick={handleSend}
                            disabled={!canSend}
                            aria-label="Send message"
                        >
                            <Send size="1em" aria-hidden="true" />
                        </button>
                    </span>
                </Tooltip>
            </div>
            <ScreengrabCapture
                open={capturing}
                onClose={() => setCapturing(false)}
                onUse={(blob) =>
                    stageFiles([new File([blob], `screenshot-${Date.now()}.png`, { type: "image/png" })])
                }
            />
        </div>
    );
};

export default ChatInput;
